import { addHeader, addHeaderWithOutBody } from "./headers";
import constants from "./api_urls";
import adminAuthService from "../services/authService";

// Log out and redirect on unauthorized responses (e.g., expired JWT)
function handleUnauthorized(status) {
  if (status === 401) {
    adminAuthService.logout();
    window.location.href = "/login";
    return true;
  }
  return false;
}

export async function apiGet(path) {
  const url = new URL((constants.isTestServer ? constants.testUrl : constants.baseUrl) + path);
  
  // Backend expects JWT Bearer token in headers, not userRole in query params
  const response = await fetch(url.toString(), await addHeaderWithOutBody("GET"));
  console.log(response);

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  const raw = await response.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = { success: false, message: raw.slice(0, 200) };
    }
  }
  return { body: data, status: response.status };
}

/** GET binary responses (e.g. PDF). Parses JSON error bodies when status !== 200. */
export async function apiGetBlob(path, accept = "application/pdf") {
  const url = new URL(
    (constants.isTestServer ? constants.testUrl : constants.baseUrl) + path
  );
  const baseOptions = await addHeaderWithOutBody("GET");
  const response = await fetch(url.toString(), {
    ...baseOptions,
    headers: {
      ...baseOptions.headers,
      Accept: accept,
    },
  });

  if (handleUnauthorized(response.status)) {
    return { blob: null, status: response.status, error: "Unauthorized" };
  }

  if (!response.ok) {
    let error = `Request failed (${response.status})`;
    try {
      const errJson = await response.json();
      error = errJson.message || errJson.error || error;
    } catch (_) {
      // ignore non-JSON error bodies
    }
    return { blob: null, status: response.status, error };
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="([^"]+)"/i);
  const filename = filenameMatch?.[1] || "AWAKEN-Summary.pdf";

  return { blob, status: response.status, filename };
}

/** POST JSON body; response is binary (e.g. PDF). */
export async function apiPostBlob(path, body, accept = "application/pdf") {
  const url = new URL(
    (constants.isTestServer ? constants.testUrl : constants.baseUrl) + path
  );
  const baseOptions = await addHeader(body, "POST");
  const response = await fetch(url.toString(), {
    ...baseOptions,
    headers: {
      ...baseOptions.headers,
      Accept: accept,
    },
  });

  if (handleUnauthorized(response.status)) {
    return { blob: null, status: response.status, error: "Unauthorized" };
  }

  if (!response.ok) {
    let error = `Request failed (${response.status})`;
    try {
      const errJson = await response.json();
      error = errJson.message || errJson.error || error;
    } catch (_) {
      // ignore non-JSON error bodies
    }
    return { blob: null, status: response.status, error };
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="([^"]+)"/i);
  const filename = filenameMatch?.[1] || "AWAKEN-Summary.pdf";

  return { blob, status: response.status, filename };
}

export async function apiGetOpenSource(uriPath) {
  const response = await fetch(uriPath, await addHeaderWithOutBody("GET"));

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  const data = await response.json();
  return { body: data, status: response.status };
}

export async function apiDelete(path) {
  const url = new URL((constants.isTestServer ? constants.testUrl : constants.baseUrl) + path);
  
  // Backend expects JWT Bearer token in headers, not userRole in query params
  const response = await fetch(url.toString(), await addHeaderWithOutBody("DELETE"));
  console.log(response);

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  const data = response.status === 200 ? await response.json() : {};
  return { body: data, status: response.status };
}

export async function apiPostPut(body, path, method) {
  const uri = (constants.isTestServer ? constants.testUrl : constants.baseUrl) + path;
  
  // Backend expects JWT Bearer token in headers, not userRole in body
  const response = await fetch(uri, await addHeader(body, method));

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  const data = await response.json();
  return { body: data, status: response.status };
}

export async function apiPostPutWithoutUrl(body, path, method) {
  const uri = path;
  
  // Backend expects JWT Bearer token in headers, not userRole in body
  const response = await fetch(uri, await addHeader(body, method));

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  const data = await response.json();
  return { body: data, status: response.status };
}

export async function apiGetFactFinder(path) {
  const url = new URL(constants.factFinderBaseUrl + path);
  const token = sessionStorage.getItem("admin_token");
  
  const headers = {
    "Content-Type": "application/json",
    "accept": "*/*",
  };
  
  // Add Bearer token if available
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  });

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  // Handle non-JSON responses (like "Token is missing")
  let data;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    const text = await response.text();
    console.error("Non-JSON response from Fact Finder API:", text);
    data = { success: false, message: text || "Unknown error" };
  }
  
  return { body: data, status: response.status };
}

export async function apiPostFactFinder(path, body) {
  const url = new URL(constants.factFinderBaseUrl + path);
  const token = sessionStorage.getItem("admin_token");
  
  const headers = {
    "Content-Type": "application/json",
    "accept": "*/*",
  };
  
  // Add Bearer token if available
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await fetch(url.toString(), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (handleUnauthorized(response.status)) {
    return { body: {}, status: response.status };
  }

  // Handle non-JSON responses
  let data;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    const text = await response.text();
    console.error("Non-JSON response from Fact Finder API:", text);
    data = { success: false, message: text || "Unknown error" };
  }
  
  return { body: data, status: response.status };
}

/**
 * Fetch all assigned admin users from Fact Finder API
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Number of records per page (default: 20)
 * @returns {Promise<{body: any, status: number}>} Response with admin users data
 */
export async function fetchFactFinderAdminUsers(page = 1, pageSize = 20) {
  const path = `/Users/GetAdminUsers?page=${page}&pageSize=${pageSize}`;
  return await apiGetFactFinder(path);
}

/**
 * Update admin import status in Fact Finder API
 * @param {number[]} ids - Array of user IDs to update
 * @param {boolean} awakenImportStatus - Import status to set (true = imported, false = not imported)
 * @returns {Promise<{body: any, status: number}>} Response from update operation
 */
export async function updateFactFinderAdminStatus(ids, awakenImportStatus) {
  const path = `/Users/UpdateAdminStatus`;
  const body = {
    ids: ids,
    awakenImportStatus: awakenImportStatus
  };
  return await apiPostFactFinder(path, body);
}
